"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { TranscriptEntry } from "@/features/quiz/coding-task-api";

/** One printed line, numbered, with who printed it. */
export type TranscriptLine = { n: number; from: TranscriptEntry["from"]; text: string };

/** The runner's chunks as lines: a chunk may hold several lines, a line never spans two speakers. */
export function transcriptLines(entries: readonly TranscriptEntry[]): TranscriptLine[] {
  const lines: TranscriptLine[] = [];
  for (const entry of entries) {
    const text = entry.text.replace(/\r\n?/g, "\n");
    const parts = text.endsWith("\n") ? text.slice(0, -1).split("\n") : text.split("\n");
    for (const part of parts) lines.push({ n: lines.length + 1, from: entry.from, text: part });
  }
  return lines;
}

const STEP_MS = 140;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * The conversation with the judge's interactor - `EX-IAC01`, the run panel's
 * board on the canvas. Each line arrives in order (one every
 * {@link STEP_MS} ms, all at once under reduced motion), the interactor's on
 * the left in green, the program's in white with an arrow out, so a reader
 * sees where the talk stopped. "Replay" plays it again; "Next" steps it.
 */
export function InteractionTranscript({
  entries,
  truncated = false,
  ended,
  programLabel,
  className,
}: {
  entries: readonly TranscriptEntry[];
  truncated?: boolean;
  /** The line under the conversation when it finished, or `null` while none applies. */
  ended?: string | null;
  /** Who "the program" is, when not the reader's own (the setter's try box names the solution). */
  programLabel?: string;
  className?: string;
}) {
  const t = useTranslations("judge.interactive");
  const lines = useMemo(() => transcriptLines(entries), [entries]);
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? lines.length : 0));
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    // A new conversation plays from the start.
    if (prefersReducedMotion()) {
      setShown(lines.length);
      setPlaying(false);
      return;
    }
    setShown(0);
    setPlaying(true);
  }, [lines]);

  useEffect(() => {
    if (!playing) return;
    if (shown >= lines.length) {
      setPlaying(false);
      return;
    }
    const timer = setTimeout(() => setShown((count) => count + 1), STEP_MS);
    return () => clearTimeout(timer);
  }, [playing, shown, lines.length]);

  const done = shown >= lines.length;

  return (
    <div className={cn("flex min-h-0 flex-col gap-2", className)}>
      <ol
        role="log"
        aria-label={t("transcriptLabel")}
        aria-live="polite"
        className="min-h-[9rem] flex-1 space-y-1 overflow-y-auto rounded-xl bg-neutral-950 p-3 font-mono text-[13px] leading-6 ring-1 ring-inset ring-white/10"
        data-testid="interaction-transcript"
      >
        {lines.length === 0 ? (
          <li className="font-sans text-xs text-white/45">{t("transcriptEmpty")}</li>
        ) : (
          lines.slice(0, shown).map((line) => {
            const judge = line.from === "interactor";
            return (
              <li
                key={line.n}
                data-from={line.from}
                className="flex items-baseline gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1"
              >
                <span className="w-6 shrink-0 text-right text-[11px] tabular-nums text-white/30">{line.n}</span>
                <span
                  className={cn(
                    "flex w-28 shrink-0 items-center gap-1 font-sans text-[11.5px]",
                    judge ? "text-emerald-300/80" : "text-[#FFAB8F]",
                  )}
                >
                  {judge ? <ArrowLeft className="h-3 w-3" aria-hidden /> : <ArrowRight className="h-3 w-3" aria-hidden />}
                  {judge ? t("judge") : (programLabel ?? t("you"))}
                </span>
                <span className={cn("min-w-0 whitespace-pre-wrap break-all", judge ? "text-emerald-200" : "text-white")}>
                  {line.text === "" ? " " : line.text}
                </span>
              </li>
            );
          })
        )}
        {done && ended ? (
          <li className="mt-2 border-t border-dashed border-white/15 pt-2 font-sans text-xs text-emerald-200/80" data-testid="transcript-ended">
            {ended}
          </li>
        ) : null}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-white/45">
          {truncated ? t("transcriptCut") : t("transcriptCount", { count: lines.length })}
        </span>
        {lines.length > 0 ? (
          <span className="flex gap-1.5">
            <Button
              type="button"
              variant="ghostDark"
              size="mini"
              disabled={done}
              onClick={() => {
                setPlaying(false);
                setShown((count) => Math.min(lines.length, count + 1));
              }}
            >
              {t("next")}
            </Button>
            <Button
              type="button"
              variant="ghostDark"
              size="mini"
              onClick={() => {
                setShown(0);
                setPlaying(true);
              }}
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              {t("replay")}
            </Button>
          </span>
        ) : null}
      </div>
    </div>
  );
}
